import { Card, Header } from "semantic-ui-react";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import articleDeliveryStatuses from "astrid-firestore/src/api/articles/constants/articleDeliveryStatuses";
import articleStatuses from "astrid-firestore/src/api/articles/constants/articleStatuses";
import articleTypes from "astrid-firestore/src/api/articles/constants/articleTypes";
import artifactValidationStatuses from "astrid-firestore/src/api/artifacts/constants/artifactValidationStatuses";
import getArticleDistributionPresets from "astrid-firestore/src/api/distribution/getArticleDistributionPresets";
import { useCollectionData } from "astrid-firestore/src/hooks";

import FlexRow from "../../../../components/Flex/FlexRow";
import Button from "../../../ui/components/Buttons/Button";
import CardContentExpandButton from "../../../ui/components/CardContentExpandButton/CardContentExpandButton";
import CopyIcon from "../../../ui/components/CopyIcon/CopyIcon";
import CoverImage from "../../../ui/components/CoverImage/CoverImage";
import DropdownMenu from "../../../ui/components/DropdownMenu/DropdownMenu";
import Label from "../../../ui/components/Label/Label";
import themeColors from "../../../ui/constants/themeColors";

import useHasRight from "../../../authorization/hooks/useHasRight";
import useHasRole from "../../../authorization/hooks/useHasRole";

import useArticleDeliveryStatuses from "../../hooks/useArticleDeliveryStatuses";
import useArticleTypeTranslations from "../../hooks/useArticleTypeTranslations";

import ArticleDistributeModal from "../ArticleDistributeModal/ArticleDistributeModal";
import ArticleEdit from "../ArticleEdit/ArticleEdit";
import ArticlePublicationStatus from "../ArticlePublicationStatus/ArticlePublicationStatus";
import ArticleReadyModal from "../ArticleReadyModal/ArticleReadyModal";
import ArticleVersionsModal from "../ArticleVersionsModal/ArticleVersionsModal";

import ArticleArtifact from "./components/ArticleArtifact";
import ArticleChannelLabel from "./components/ArticleChannelLabel";
import ArticleChannels from "./components/ArticleChannels";
import ArticleDistributionsModal from "./components/ArticleDistributionsModal";
import ArticleExports from "./components/ArticleExports";

export default function Article({ work, article, publisher, artifacts, channels, overlay, production }) {
	const { t } = useTranslation();
	const hasRight = useHasRight();
	const hasRole = useHasRole();
	const isSuperAdmin = hasRole("owner", "admin");

	const [edit, setEdit] = useState(false);
	const [openVersions, setOpenVersions] = useState(false);
	const [expand, setExpand] = useState(false);
	const [deliver, setDeliver] = useState(false);
	const [ready, setReady] = useState(false);
	const [distributionHistory, setDistributionHistory] = useState(false);

	const [versions] = useCollectionData(article.ref.collection("versions").limit(1));

	const articleTypeTranslations = useArticleTypeTranslations();

	const { presetChannels, enabledChannels } = getArticleDistributionPresets({ article, publisher, channels });

	const { color, active, disabled } = useArticleDeliveryStatuses()[article.delivery.status];

	const selectedArtifact = artifacts?.find((artifact) => artifact.id === article.artifact?.id);
	const archivedArtifacts = artifacts?.filter((a) => a.id !== selectedArtifact?.id) || [];

	const isDeliveryRequired = [
		articleDeliveryStatuses.DELIVERY_NEEDED,
		articleDeliveryStatuses.DELIVERY_FAILED,
	].includes(article.delivery.status);

	const dropdownMenuOptions = [
		{
			key: "distributionHistory",
			value: "distributionHistory",
			text: t("distributionHistory", "Distribution history"),
			hidden: !isSuperAdmin,
			onClick: () => setDistributionHistory(true),
		},
		{
			key: "versionHistory",
			value: "versionHistory",
			text: t("versionHistory", "Version history"),
			disabled: !(versions?.length > 0),
			onClick: () => setOpenVersions(true),
		},
	];

	return (
		<>
			<Card
				fluid
				raised={edit}
				attached="top"
				style={{
					outline: edit && `3px solid ${themeColors.BLUE_FADED}`,
				}}
			>
				<Card.Content>
					<FlexRow style={{ gap: "1em", alignItems: "flex-start", justifyContent: "flex-start" }}>
						<div style={{ height: 80, width: 80 }}>
							<CoverImage modal thumb rounded data={article} size={80} style={{ margin: "0 auto" }} />
						</div>

						<div>
							<Header as="h3">
								{article.name}

								<Header.Subheader>
									{articleTypeTranslations[article.type]} [{article.isbn}{" "}
									<CopyIcon value={article.isbn} />]{" "}
									{isSuperAdmin && <span style={{ opacity: 0.6 }}>({article.id})</span>}
								</Header.Subheader>
							</Header>

							<FlexRow style={{ flexWrap: "wrap", alignItems: "center", gap: "0.4em" }}>
								<ArticlePublicationStatus article={article} />

								{!production && (
									<ArticleArtifact
										work={work}
										article={article}
										selectedArtifact={selectedArtifact}
										archivedArtifacts={archivedArtifacts}
										publisher={publisher}
									/>
								)}

								{[articleTypes.AUDIOBOOK, articleTypes.PODCAST].includes(article.type) &&
									selectedArtifact?.status === artifactValidationStatuses.VALID && (
										<ArticleExports
											article={article}
											selectedArtifact={selectedArtifact}
											publisher={publisher}
										/>
									)}

								{article.status === articleStatuses.DRAFT && <Label>{t("draft", "Draft")}</Label>}
							</FlexRow>
						</div>

						<FlexRow style={{ gap: 10, alignItems: "center", marginLeft: "auto" }}>
							{hasRight("articles.view") && (
								<>
									<Button size="small" disabled={edit} onClick={() => setEdit(true)}>
										{t("edit", "Edit")}
									</Button>

									{article.status === articleStatuses.DRAFT && (
										<Button primary size="small" onClick={() => setReady(true)}>
											{t("readyToDeliver", "Ready to deliver")}
										</Button>
									)}

									{article.status === articleStatuses.READY && (
										<Button
											size="small"
											color={isDeliveryRequired ? color : "grey"}
											disabled={disabled}
											loading={active}
											onClick={() => {
												setDeliver(true);
												setEdit(false);
											}}
										>
											{t("deliver", "Deliver")}
										</Button>
									)}
								</>
							)}

							<DropdownMenu style={{ marginRight: -5 }} options={dropdownMenuOptions} />
						</FlexRow>
					</FlexRow>
				</Card.Content>

				<>
					<CardContentExpandButton expand={expand} onClick={() => setExpand((expand) => !expand)} />

					<Card.Content style={{ padding: 0 }}>
						{!expand && (
							<div style={{ display: "flex", flexWrap: "wrap", gap: "0.4em", padding: "1em" }}>
								{enabledChannels.map((channel) => (
									<ArticleChannelLabel key={channel.id} article={article} channel={channel} />
								))}

								<Label basic onClick={() => setExpand(true)} style={{ cursor: "pointer" }}>
									{t("showMore", "Show more")}
								</Label>
							</div>
						)}

						{expand && (
							<div style={{ display: "flex", gap: "0.4em" }}>
								<ArticleChannels article={article} publisher={publisher} channels={presetChannels} />
							</div>
						)}
					</Card.Content>
				</>
			</Card>

			{edit && (
				<ArticleEdit
					work={work}
					overlay={overlay}
					article={article}
					channels={channels}
					publisher={publisher}
					onClose={() => setEdit(false)}
				/>
			)}

			{ready && <ArticleReadyModal article={article} onClose={() => setReady(false)} />}

			{deliver && (
				<ArticleDistributeModal
					publisher={publisher}
					articles={[article]}
					channels={presetChannels}
					onClose={() => setDeliver(false)}
				/>
			)}

			{openVersions && (
				<ArticleVersionsModal article={article} channels={channels} onClose={() => setOpenVersions(false)} />
			)}

			{distributionHistory && (
				<ArticleDistributionsModal article={article} onClose={() => setDistributionHistory(false)} />
			)}
		</>
	);
}
